/**
 * React Query hooks + types for the changelog ("What's new").
 *
 * Two surfaces consume this file. IT authors entries at `/it/changelog` (gated
 * by the `it.changelog` accessSlug), and every signed-in user reads them at
 * `/changelog`, reached from the avatar menu. Backend: api#541, migration 0153,
 * routes under `/v1/core/changelog`.
 *
 * Mirrors the react-query patterns in `useContent.ts`.
 */

import { useEffect, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { apiGet, apiPost, apiDelete } from './api';
import { API_BASE } from './apiBase';
import { COCKPIT_DEPARTMENTS, isLaunched } from '@/data/tools.config';

export const CHANGELOG_API_BASE = `${API_BASE}/v1/core/changelog`;

export const CHANGELOG_QUERY_KEY = ['changelog'] as const;

export function hasChangelogAdmin(perms: string[] | undefined, superAdmin = false): boolean {
  if (superAdmin) return true;
  return !!perms?.includes('it.changelog');
}

// --------------------------------------------------------------------------
// Types
// --------------------------------------------------------------------------

/** Which app an entry is published to. `both` shows in Cockpit and Sweet Spot. */
export type ChangelogApp = 'cockpit' | 'sweetspot' | 'both';
/** Which app a reader is asking as. `both` is a write-side target only. */
export type ChangelogReaderApp = 'cockpit' | 'sweetspot';

export const CHANGELOG_APPS: ChangelogApp[] = ['cockpit', 'sweetspot', 'both'];

export const CHANGELOG_APP_LABELS: Record<ChangelogApp, string> = {
  cockpit: 'Cockpit',
  sweetspot: 'Sweet Spot',
  both: 'Both',
};

export type ChangelogToolRef = {
  app: ChangelogReaderApp;
  tool_id: string;
  tool_label: string;
};

export type ChangelogEntry = {
  id: number;
  app: ChangelogApp;
  title: string;
  body_html: string;
  /** True when the entry covers the whole app, so `tools` is empty. */
  all_tools: boolean;
  published_at: string;
  author_email: string;
  tools: ChangelogToolRef[];
};

// --------------------------------------------------------------------------
// Tool options for the picker
// --------------------------------------------------------------------------

/**
 * The Sweet Spot tool list.
 *
 * Source of truth is `sweetspot/src/shell/navDefs.ts` (`NAV_PAGES_RAW`), which
 * this repo cannot import. Mirrored here because the Cockpit authoring page is
 * the one place that offers both apps' tools in a single picker. Keep in step
 * when a Sweet Spot page is added or renamed; the cost of drift is a missing or
 * stale OPTION, never a broken entry, because the label chosen at publish time
 * is stored on the entry itself.
 */
export const SWEETSPOT_TOOL_OPTIONS: { tool_id: string; tool_label: string }[] = [
  { tool_id: 'home', tool_label: 'Home' },
  { tool_id: 'today', tool_label: 'Today' },
  { tool_id: 'inventory', tool_label: 'Inventory' },
  { tool_id: 'pulse', tool_label: 'Pulse' },
  { tool_id: 'sampling', tool_label: 'Sampling' },
  { tool_id: 'performance', tool_label: 'Performance' },
  { tool_id: 'promotions', tool_label: 'Promotions' },
  { tool_id: 'loyalty', tool_label: 'Loyalty' },
  { tool_id: 'recognition', tool_label: 'Recognition' },
  { tool_id: 'training', tool_label: 'Resources' },
  { tool_id: 'my-store', tool_label: 'My Store' },
  { tool_id: 'comms', tool_label: 'Comms' },
  { tool_id: 'schedule', tool_label: 'Schedule' },
  { tool_id: 'directory', tool_label: 'Directory' },
  { tool_id: 'chat', tool_label: 'Chat' },
];

/** One selectable tool in the picker, carrying the app it belongs to. */
export type ChangelogToolOption = ChangelogToolRef & {
  /** Group heading in the picker: the Cockpit department, or 'Sweet Spot'. */
  group: string;
};

/** A stable key for a tool option, since ids collide across the two apps. */
export function changelogToolKey(t: { app: ChangelogReaderApp; tool_id: string }): string {
  return `${t.app}:${t.tool_id}`;
}

/**
 * The tools a picker should offer for the selected target app. Cockpit's come
 * live from the catalog (launched tools only, so a hidden tool is never
 * offered); Sweet Spot's come from the mirrored list above. A `both` entry
 * offers every tool in both apps, which is the shared-UI-component case.
 */
/** Tool id recorded for a "New tool" choice; the label says which app. */
export const NEW_TOOL_ID = 'new-tool';

export function changelogToolOptions(app: ChangelogApp): ChangelogToolOption[] {
  const out: ChangelogToolOption[] = [];
  // A brand-new tool is not in either list until its own entry ships, so each
  // app offers a "New tool" choice at the top of its group.
  if (app === 'cockpit' || app === 'both') {
    out.push({ app: 'cockpit', tool_id: NEW_TOOL_ID, tool_label: 'New tool', group: 'Cockpit' });
  }
  if (app === 'sweetspot' || app === 'both') {
    out.push({ app: 'sweetspot', tool_id: NEW_TOOL_ID, tool_label: 'New tool', group: 'Sweet Spot' });
  }
  if (app === 'cockpit' || app === 'both') {
    for (const dept of COCKPIT_DEPARTMENTS) {
      for (const tool of dept.tools) {
        if (!isLaunched(tool)) continue;
        out.push({
          app: 'cockpit',
          tool_id: tool.id,
          tool_label: tool.name,
          group: dept.name,
        });
      }
    }
  }
  if (app === 'sweetspot' || app === 'both') {
    for (const t of SWEETSPOT_TOOL_OPTIONS) {
      out.push({ app: 'sweetspot', ...t, group: 'Sweet Spot' });
    }
  }
  return out;
}

// --------------------------------------------------------------------------
// Query hooks
// --------------------------------------------------------------------------

type EntriesResponse = { entries: ChangelogEntry[] };

/**
 * The entries a reader in `app` should see. Tolerates a 404 so the page renders
 * an empty state rather than an error while the backend ships in parallel.
 */
export function useChangelog(app: ChangelogReaderApp): UseQueryResult<EntriesResponse, Error> {
  return useQuery<EntriesResponse, Error>({
    queryKey: [...CHANGELOG_QUERY_KEY, app],
    queryFn: () => apiGet<EntriesResponse>(`${CHANGELOG_API_BASE}?app=${app}`),
    staleTime: 60_000,
    retry: false,
  });
}

// --------------------------------------------------------------------------
// Unread dot for the header "What's new" button
// --------------------------------------------------------------------------

export const CHANGELOG_SEEN_KEY = 'cockpit.changelog.seen_at';
const CHANGELOG_SEEN_EVENT = 'cockpit:changelog-seen';

/** The newest `published_at` in a list of entries, or null when there are none. */
export function newestPublishedAt(entries: Pick<ChangelogEntry, 'published_at'>[] | undefined): string | null {
  let best: string | null = null;
  let bestMs = Number.NEGATIVE_INFINITY;
  for (const e of entries ?? []) {
    const ms = Date.parse(e.published_at);
    if (Number.isFinite(ms) && ms > bestMs) {
      bestMs = ms;
      best = e.published_at;
    }
  }
  return best;
}

/**
 * True when the newest entry was published after the last time this person
 * opened /changelog. No entries means nothing to read. Entries with nothing
 * recorded as seen yet count as unread.
 */
export function hasUnreadChangelog(newest: string | null, seenAt: string | null): boolean {
  if (!newest) return false;
  const newestMs = Date.parse(newest);
  if (!Number.isFinite(newestMs)) return false;
  if (!seenAt) return true;
  const seenMs = Date.parse(seenAt);
  if (!Number.isFinite(seenMs)) return true;
  return newestMs > seenMs;
}

export function readChangelogSeenAt(): string | null {
  try {
    return window.localStorage.getItem(CHANGELOG_SEEN_KEY);
  } catch {
    return null;
  }
}

/** Record that everything up to `publishedAt` has been seen, and tell the header. */
export function writeChangelogSeenAt(publishedAt: string): void {
  try {
    window.localStorage.setItem(CHANGELOG_SEEN_KEY, publishedAt);
  } catch {
    /* storage blocked: the dot just stays until the next visit */
  }
  window.dispatchEvent(new Event(CHANGELOG_SEEN_EVENT));
}

/** Whether to show the unread dot. Updates when /changelog records a visit. */
export function useChangelogUnread(): boolean {
  const { data } = useChangelog('cockpit');
  const [seenAt, setSeenAt] = useState<string | null>(readChangelogSeenAt);
  useEffect(() => {
    const sync = () => setSeenAt(readChangelogSeenAt());
    window.addEventListener(CHANGELOG_SEEN_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CHANGELOG_SEEN_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);
  return hasUnreadChangelog(newestPublishedAt(data?.entries), seenAt);
}

// --------------------------------------------------------------------------
// Mutations
// --------------------------------------------------------------------------

export type CreateChangelogBody = {
  app: ChangelogApp;
  title: string;
  body_html: string;
  all_tools: boolean;
  tools: ChangelogToolRef[];
};

export function useCreateChangelogEntry() {
  const qc = useQueryClient();
  return useMutation<{ ok: true; id: number }, Error, CreateChangelogBody>({
    mutationFn: (body) => apiPost<{ ok: true; id: number }>(CHANGELOG_API_BASE, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHANGELOG_QUERY_KEY });
    },
  });
}

export function useDeleteChangelogEntry() {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: async (id: number) => {
      await apiDelete<unknown>(`${CHANGELOG_API_BASE}/${id}`);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CHANGELOG_QUERY_KEY });
    },
  });
}
